import React, { useState } from 'react';
import { Modal, Form, Input, message, Checkbox, Space, Select, Row, Col, Typography } from 'antd';
import { api } from '@common/api';
import { InputMask } from '@components/atoms';

const { Title } = Typography;
const { TextArea } = Input;

interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  vehicleType?: string[];
  chains?: string[];
  hardwareParts?: string[];
  chainSize?: string[];
  tireSize?: string[];
  message: string;
}

interface ContactExpertsModalProps {
  visible: boolean;
  onClose: () => void;
  productName?: string;
  source?: 'merch' | 'product-detail' | 'landing';
}

export const ContactExpertsModal: React.FC<ContactExpertsModalProps> = ({
  visible,
  onClose,
  productName,
  source = 'merch',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();

  const handleFinish = async (values: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      // Format the message to include all product interests
      let productInterests = '';
      
      if (values.vehicleType && values.vehicleType.length > 0) {
        productInterests += `\n\nVehicle Type:\n${values.vehicleType.join(', ')}`;
      }
      
      if (values.chains && values.chains.length > 0) {
        productInterests += `\n\nChains:\n${values.chains.join(', ')}`;
      }
      
      if (values.hardwareParts && values.hardwareParts.length > 0) {
        productInterests += `\n\nHardware and Parts:\n${values.hardwareParts.join(', ')}`;
      }
      
      if (values.chainSize && values.chainSize.length > 0) {
        productInterests += `\n\nChain Size:\n${values.chainSize.join(', ')}`;
      }
      
      if (values.tireSize && values.tireSize.length > 0) {
        productInterests += `\n\nTire Size:\n${values.tireSize.join(', ')}`;
      }
      
      // Build subject based on source
      let subject = 'Product Inquiry';
      if (source === 'product-detail' && productName) {
        subject = `Product Inquiry - ${productName}`;
      } else if (source === 'merch') {
        subject = 'Product Inquiry from Shop';
      } else if (source === 'landing') {
        subject = 'Product Inquiry from Landing Page';
      }
      
      const dataToSend = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        subject,
        message: `${values.message}${productInterests}`,
      };
      
      await api.post('/contact', dataToSend);
      form.resetFields();
      onClose();
      message.success('Your request has been sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      message.error('Unable to send your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <Title level={3} className="font-orator font-bold text-orangicide uppercase">
          request information
        </Title>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={<span className="text-orangicide text-xl">X</span>}
      centered
      width={800}
      styles={{
        mask: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.7)' },
        content: { backgroundColor: '#181717', border: '1px solid #3f3f46', borderRadius: '0px' },
        header: { backgroundColor: '#181717', borderBottom: '1px solid #3f3f46' },
        body: { padding: '12px 8px' },
      }}
    >
      <Form
        form={form}
        name="contact-experts-form"
        layout="vertical"
        onFinish={handleFinish}
        requiredMark={false}
        disabled={isSubmitting}
        className="max-h-[85vh] overflow-y-auto overflow-x-hidden"
        style={{
          margin: 0,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {/* Basic Contact Information */}
        <Form.Item
          label={<span className="font-orator font-semibold text-white">FULL NAME</span>}
          name="name"
          rules={[{ required: true, message: 'please enter your name' }]}
        >
          <Input
            className="bg-zinc-800 font-orator border-zinc-700 text-white"
            placeholder="Your full name"
          />
        </Form.Item>

        <Form.Item
          label={<span className="font-orator font-semibold text-white">EMAIL ADDRESS</span>}
          name="email"
          rules={[
            { required: true, message: 'please enter your email' },
            { type: 'email', message: 'please enter a valid email' },
          ]}
        >
          <Input
            className="bg-zinc-800 font-orator border-zinc-700 text-white"
            placeholder="Your email address"
          />
        </Form.Item>

        <Form.Item
          label={<span className="font-orator font-semibold text-white">PHONE NUMBER</span>}
          name="phone"
          rules={[
            { required: true, message: 'please enter your phone number' },
            {
              pattern: /^(\+1\s?)?(\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/,
              message: 'please enter a valid US phone number',
            },
          ]}
        >
          <InputMask
            mask="usphone"
            className="bg-zinc-800 font-orator border-zinc-700 text-white"
            placeholder="(555) 123-4567"
          />
        </Form.Item>

        {/* Desired Products Section */}
        <div className="pt-4 pb-2">
          <Title level={4} className="font-orator text-white font-semibold uppercase !mb-0">
            Desired Products
          </Title>
        </div>

        <div style={{ paddingLeft: 10, marginRight: 0, overflow: 'hidden' }}>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              {/* Vehicle Type */}
              <Form.Item
                label={<span className="font-orator text-orangicide font-semibold">VEHICLE TYPE</span>}
                name="vehicleType"
              >
                <Checkbox.Group className="w-full">
                  <Space direction="vertical" className="w-full">
                    <Checkbox value="Trackless" className="font-orator text-white">
                      Trackless
                    </Checkbox>
                    <Checkbox value="Trucks and Busses" className="font-orator text-white">
                      Trucks and Busses
                    </Checkbox>
                    <Checkbox value="Heavy Equipment" className="font-orator text-white">
                      Heavy Equipment
                    </Checkbox>
                    <Checkbox value="Other" className="font-orator text-white">
                      Other
                    </Checkbox>
                  </Space>
                </Checkbox.Group>
              </Form.Item>

              {/* Chains */}
              <Form.Item
                label={<span className="font-orator text-orangicide font-semibold">CHAINS</span>}
                name="chains"
              >
                <Checkbox.Group className="w-full">
                  <Space direction="vertical" className="w-full">
                    <Checkbox value="Bulk Cross Chain" className="font-orator text-white">
                      Bulk Cross Chain
                    </Checkbox>
                    <Checkbox value="Bulk Side Chain" className="font-orator text-white">
                      Bulk Side Chain
                    </Checkbox>
                    <Checkbox value="Completed Cross Chain" className="font-orator text-white">
                      Completed Cross Chain
                    </Checkbox>
                    <Checkbox value="Other" className="font-orator text-white">
                      Other
                    </Checkbox>
                  </Space>
                </Checkbox.Group>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              {/* Hardware and Parts */}
              <Form.Item
                label={<span className="font-orator text-orangicide font-semibold">HARDWARE AND PARTS</span>}
                name="hardwareParts"
              >
                <Checkbox.Group className="w-full">
                  <Space direction="vertical" className="w-full">
                    <Checkbox value="Fasteners" className="font-orator text-white">
                      Fasteners
                    </Checkbox>
                    <Checkbox value="Hooks" className="font-orator text-white">
                      Hooks
                    </Checkbox>
                    <Checkbox value="Repair Links & Shackles" className="font-orator text-white">
                      Repair Links & Shackles
                    </Checkbox>
                    <Checkbox value="Tensioners" className="font-orator text-white">
                      Tensioners
                    </Checkbox>
                    <Checkbox value="Chain Tools" className="font-orator text-white">
                      Chain Tools
                    </Checkbox>
                    <Checkbox value="Other" className="font-orator text-white">
                      Other
                    </Checkbox>
                  </Space>
                </Checkbox.Group>
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Size Selectors */}
        <div style={{ marginLeft: 0, marginRight: 0, overflow: 'hidden' }}>
          <Row gutter={12}>
            <Col xs={24} sm={12}>
              {/* Chain Size */}
              <Form.Item
                label={<span className="font-orator text-orangicide font-semibold">CHAIN SIZE</span>}
                name="chainSize"
              >
                <Select
                  mode="multiple"
                  placeholder="Select chain size(s)"
                  className="w-full custom-select"
                  popupClassName="custom-select-dropdown"
                  options={[
                    { label: '7mm', value: '7mm' },
                    { label: '8mm', value: '8mm' },
                    { label: '9.5mm', value: '9.5mm' },
                    { label: '11mm', value: '11mm' },
                    { label: '12mm', value: '12mm' },
                    { label: '14mm', value: '14mm' },
                    { label: 'Other', value: 'Other' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              {/* Tire Size */}
              <Form.Item
                label={<span className="font-orator text-orangicide font-semibold">TIRE SIZE</span>}
                name="tireSize"
              >
                <Select
                  mode="multiple"
                  placeholder="Select tire size(s)"
                  className="w-full custom-select"
                  popupClassName="custom-select-dropdown"
                  options={[
                    { label: '14-17.5', value: '14-17.5' },
                    { label: '17.5-25', value: '17.5-25' },
                    { label: '20.5-25', value: '20.5-25' },
                    { label: '23.5-25', value: '23.5-25' },
                    { label: '26.5-25', value: '26.5-25' },
                    { label: '29.5-25', value: '29.5-25' },
                    { label: '17.5-24', value: '17.5-24' },
                    { label: '12.5/80-18', value: '12.5/80-18' },
                    { label: '11-24.5', value: '11-24.5' },
                    { label: '11-22.5', value: '11-22.5' },
                    { label: '225/75-16', value: '225/75-16' },
                    { label: '365/80-20', value: '365/80-20' },
                    { label: '480/85-34', value: '480/85-34' },
                    { label: '340/80-18', value: '340/80-18' },
                    { label: '12-16.5', value: '12-16.5' },
                    { label: '16-24', value: '16-24' },
                    { label: '275/70-18', value: '275/70-18' },
                    { label: '14-24', value: '14-24' },
                    { label: 'Other', value: 'Other' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Message */}
        <Form.Item
          label={<span className="font-orator font-semibold text-white">MESSAGE</span>}
          name="message"
          rules={[{ required: true, message: 'please enter your message' }]}
        >
          <TextArea
            rows={4}
            className="bg-zinc-800 font-orator text-white"
            placeholder="Your message"
          />
        </Form.Item>

        {/* Submit Button */}
        <div className="text-center pt-4 pb-4">
          <button
            type="submit"
            className="btn-orangicide font-displayed text-xl tracking-wider w-50 h-10 cursor-pointer"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
          </button>
        </div>
      </Form>
    </Modal>
  );
};

